// @flow
/**
 * Curriculum restrictions stop instructors creating new courses and scheduling
 * new classes on a curriculum (e.g. CCHDF-CI). The API decides what is
 * restricted, including per-instructor exemptions, and exposes it as:
 *
 *   courses:   canScheduleClasses, restrictedAt
 *   curricula: isRestricted, restrictedAt  (instructor curricula endpoint only)
 *
 * restrictedAt is set only when the instructor is affected, so a future value
 * means "heads up", and a past one means the restriction is now in effect even
 * if the page was loaded before the cutoff.
 */

export const RESTRICTED = "restricted";
export const UPCOMING = "upcoming";

export type RestrictionStatus = typeof RESTRICTED | typeof UPCOMING;

export type RestrictionNotice = {
  status: RestrictionStatus,
  title: string,
  message: string,
};

// Restriction dates are set in the API's time zone, so show them in it too.
// Otherwise a midnight cutoff reads as the day before for anyone further west.
const RESTRICTION_TIME_ZONE = "America/Chicago";

const SUPPORT =
  "If you have questions, contact USCCA instructor support at 1-877-577-4800.";

export const formatRestrictionDate = (restrictedAt: string): string =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: RESTRICTION_TIME_ZONE,
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(restrictedAt));

export const getRestrictionStatus = (
  isBlocked: boolean,
  restrictedAt: ?string,
  now: Date = new Date()
): ?RestrictionStatus => {
  if (isBlocked) {
    return RESTRICTED;
  }

  if (!restrictedAt) {
    return null;
  }

  const startsAt = new Date(restrictedAt);

  if (Number.isNaN(startsAt.getTime())) {
    return null;
  }

  return startsAt.getTime() <= now.getTime() ? RESTRICTED : UPCOMING;
};

export const getCourseRestrictionNotice = (
  {
    canScheduleClasses = true,
    restrictedAt,
  }: { canScheduleClasses?: boolean, restrictedAt?: ?string },
  now: Date = new Date()
): ?RestrictionNotice => {
  const status = getRestrictionStatus(!canScheduleClasses, restrictedAt, now);

  if (status === RESTRICTED) {
    return {
      status,
      title: "New classes can no longer be scheduled",
      message: `This course's curriculum is no longer available for new classes. Existing classes are not affected and students can still register. ${SUPPORT}`,
    };
  }

  if (status === UPCOMING && restrictedAt) {
    return {
      status,
      title: `New classes can't be scheduled after ${formatRestrictionDate(
        restrictedAt
      )}`,
      message: `Starting ${formatRestrictionDate(
        restrictedAt
      )}, this course's curriculum will no longer be available for new classes. Classes already scheduled are not affected. ${SUPPORT}`,
    };
  }

  return null;
};

export const getCurriculumOptionRestriction = (
  {
    isRestricted = false,
    restrictedAt,
  }: {
    isRestricted?: boolean,
    restrictedAt?: ?string,
  },
  now: Date = new Date()
): { isDisabled: boolean, labelSuffix: string } => {
  const status = getRestrictionStatus(isRestricted, restrictedAt, now);

  if (status === RESTRICTED) {
    return { isDisabled: true, labelSuffix: " (no longer available)" };
  }

  if (status === UPCOMING && restrictedAt) {
    return {
      isDisabled: false,
      labelSuffix: ` (unavailable after ${formatRestrictionDate(
        restrictedAt
      )})`,
    };
  }

  return { isDisabled: false, labelSuffix: "" };
};
